import {useLayoutEffect, useRef} from 'react';

import {AnimatedHeight} from './AnimatedHeight';
import {modalSizeVariants} from './modal.config';
import {ModalStepsFooter} from './ModalStepsFooter';
import {ModalTabsHeader} from './ModalTabsHeader';
import {useDiscardGuard} from './useDiscardGuard';
import {cn} from '../../lib/utils';
import {Dialog, DialogContent, DialogFooter, DialogTitle} from '../shadcn/dialog';

import type {ModalOutsidePress, ModalSize} from './modal.types';
import type {CustomButtonConfig} from './ModalStepsFooter';
import type {TabItem} from '../Tabs/tab.types';
import type React from 'react';
import type {ReactNode} from 'react';

export interface MultiStepsModalProps {
  /** Controls open state (controlled mode) */
  isOpen?: boolean;
  /** Called when the modal requests to be closed */
  onOpenChange?: (open: boolean) => void;
  /** Body content */
  children?: ReactNode;
  /** Additional class on the dialog content panel */
  className?: string;
  /** Max width of the panel, from the shared modal size scale. Defaults to `xl`. */
  size?: ModalSize;
  /** Disable focus trap and scroll lock. Set to true in Storybook to keep docs interactive. */
  disableFocusTrap?: boolean;
  /** Container element for the portal. */
  portalContainer?: React.RefObject<HTMLElement | null> | HTMLElement | null;
  /** Accessible title for screen readers (not displayed visually) */
  title?: ReactNode;
  /** Ordered list of tabs — drives the header and next/back navigation */
  tabs: TabItem[];
  /** Controlled active tab value — must match one of tabs[n].value */
  activeTab: string;
  /** Called with the new tab value when the user clicks Next, Back, or a tab trigger */
  onTabChange: (value: string) => void;
  /** Label for the Next button. Defaults to "Next". */
  nextLabel?: ReactNode;
  /** Disable the Next button (e.g. form is invalid) */
  nextDisabled?: boolean;
  /** Shows a spinner on the Next button and disables it */
  nextIsLoading?: boolean;
  /** Hide the chevron icon on the Next button */
  nextHideIcon?: boolean;
  /** Label for the Back button. Defaults to "Back". */
  backLabel?: ReactNode;
  /** Disable the Back button */
  backDisabled?: boolean;
  /** Custom buttons rendered between Back and Next in the footer */
  customButtons?: CustomButtonConfig[];
  /**
   * Override the Next button click handler. When provided, the built-in
   * tab-advance is skipped and this callback is called instead.
   * The Next button is still hidden on the last tab unless this is provided.
   */
  onNextClick?: () => void;
  /**
   * Override the Back button click handler. When provided, the built-in
   * tab-back is skipped and this callback is called instead.
   * The Back button is still hidden on the first tab unless this is provided.
   */
  onBackClick?: () => void;
  /**
   * Optional sticky element rendered between the tabs header and the scrollable body.
   * Use this for sub-step navigation bars (e.g. <ModalSubNav />).
   */
  subHeader?: ReactNode;
  /** Extra key that resets body scroll (with `activeTab`) — pass the sub-step for tabs with internal steps. */
  scrollResetKey?: string;
  /** Removes the body's side padding so content bleeds to the modal edges. */
  isBodyFlush?: boolean;
  /** Removes the footer entirely — for a terminal state (e.g. a confirmation) with nothing left to do. */
  hideFooter?: boolean;
  outsidePress?: ModalOutsidePress;
  isDirty?: boolean;
  discardDescription?: string;
  backClosesModal?: boolean;
}

export function MultiStepsModal({
  isOpen,
  onOpenChange,
  children,
  className,
  size = 'xl',
  disableFocusTrap = false,
  portalContainer,
  title,
  tabs,
  activeTab,
  onTabChange,
  nextLabel,
  nextDisabled,
  nextIsLoading,
  nextHideIcon,
  backLabel,
  backDisabled,
  customButtons,
  onNextClick,
  onBackClick,
  subHeader,
  scrollResetKey,
  isBodyFlush = false,
  hideFooter = false,
  outsidePress,
  isDirty = false,
  discardDescription,
  backClosesModal = false
}: MultiStepsModalProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const {requestClose, handleOpenChange, discardModal} = useDiscardGuard({
    isOpen,
    isDirty,
    onOpenChange,
    discardDescription
  });
  const currentIndex = tabs.findIndex(t => t.value === activeTab);
  const isInvalidIndex = currentIndex === -1;
  const isFirst = isInvalidIndex || !tabs.slice(0, currentIndex).some(t => !t.disabled);
  const isLast = isInvalidIndex || !tabs.slice(currentIndex + 1).some(t => !t.disabled);

  useLayoutEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [activeTab, scrollResetKey]);

  const handleNext = () => {
    if (onNextClick) return onNextClick();
    if (isInvalidIndex) return;
    const next = tabs.slice(currentIndex + 1).find(t => !t.disabled);
    if (next) onTabChange(next.value);
  };

  const handleBack = () => {
    if (backClosesModal) return requestClose();
    if (onBackClick) return onBackClick();
    if (isInvalidIndex) return;
    const prev = [...tabs.slice(0, currentIndex)].reverse().find(t => !t.disabled);
    if (prev) onTabChange(prev.value);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      modal={!disableFocusTrap}
      outsidePress={outsidePress}
    >
      <DialogContent
        className={cn('overflow-hidden pb-0 gap-0', modalSizeVariants({size}), className)}
        initialFocus={disableFocusTrap ? false : undefined}
        container={portalContainer}
      >
        {title && <DialogTitle className="sr-only">{title}</DialogTitle>}
        <ModalTabsHeader tabs={tabs} value={activeTab} onValueChange={onTabChange} />
        {subHeader}
        {children && (
          <div
            ref={bodyRef}
            data-slot="modal-body"
            className={cn('-mx-6 min-h-0 flex-1 overflow-y-auto', isBodyFlush ? 'px-0' : 'px-6')}
          >
            {/* overflow-clip keeps sticky descendants working; the px/-mx pair leaves room for rings
                and needs body padding to absorb it, so a flush body skips it. */}
            <AnimatedHeight className={cn('overflow-clip', !isBodyFlush && '-mx-1.5 px-1.5')}>
              {children}
            </AnimatedHeight>
          </div>
        )}
        {!hideFooter && (
          <DialogFooter className="-mx-6">
            <ModalStepsFooter
              onBack={!isFirst || onBackClick || backClosesModal ? handleBack : undefined}
              onNext={!isLast || onNextClick ? handleNext : undefined}
              backLabel={backLabel}
              nextLabel={nextLabel}
              nextDisabled={nextDisabled}
              nextIsLoading={nextIsLoading}
              nextHideIcon={nextHideIcon}
              backDisabled={backDisabled}
              customButtons={customButtons}
              className="px-6 pb-6"
            />
          </DialogFooter>
        )}
        {discardModal}
      </DialogContent>
    </Dialog>
  );
}
